import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';

/**
 * Two-language dictionary.
 *
 * Deliberately not i18next: there are exactly two languages, no pluralisation
 * rules beyond simple counts and no runtime locale loading. A typed record is
 * smaller, has no dependency, and -- because the key type is derived from the
 * English table -- a missing Hindi string is a compile error rather than a
 * stray English word in the middle of a Hindi screen.
 */

export const EN = {
  'app.name': 'Patta Abhilekh',
  'app.org': 'Nagar Nigam Jaipur',
  'app.tagline': 'File and Patta Record Tracking',

  'nav.dashboard': 'Dashboard',
  'nav.search': 'Search files',
  'nav.locations': 'Record rooms',
  'nav.issued': 'Issued files',
  'nav.import': 'Import',
  'nav.users': 'Users',
  'nav.reports': 'Reports',
  'nav.audit': 'Audit trail',

  'action.search': 'Search',
  'action.cancel': 'Cancel',
  'action.save': 'Save',
  'action.saving': 'Saving…',
  'action.close': 'Close',
  'action.issue': 'Issue file',
  'action.return': 'Return file',
  'action.back': 'Back',
  'action.signIn': 'Sign in',
  'action.signOut': 'Sign out',
  'action.retry': 'Try again',
  'action.clear': 'Clear',
  'action.edit': 'Edit',
  'action.viewAll': 'View all',
  'action.download': 'Download',
  'action.next': 'Next',
  'action.previous': 'Previous',
  'action.loadMore': 'Load more',
  'action.print': 'Print',

  'status.AVAILABLE': 'Available',
  'status.ISSUED': 'Issued',
  'status.MISSING': 'Missing',
  'status.WEEDED': 'Weeded out',
  'status.overdue': 'Overdue',

  'kind.GENERAL': 'General file',
  'kind.PROPERTY': 'Property file',

  'field.eFileNumber': 'E-file number',
  'field.fileNumber': 'File number',
  'field.subject': 'Subject',
  'field.allottee': 'First allottee',
  'field.plotNo': 'Plot no.',
  'field.scheme': 'Scheme',
  'field.society': 'Society',
  'field.ward': 'Ward',
  'field.zone': 'Zone',
  'field.department': 'Department',
  'field.year': 'Year',
  'field.location': 'Location',
  'field.status': 'Status',
  'field.leaseAcNo': 'Lease A/C no.',
  'field.remark': 'Remark',
  'field.room': 'Room',
  'field.rack': 'Rack',
  'field.column': 'Column',
  'field.bag': 'Bag',
  'field.purpose': 'Purpose',
  'field.expectedReturn': 'Expected return',
  'field.issuedTo': 'Issued to',
  'field.issuedBy': 'Issued by',
  'field.issuedOn': 'Issued on',
  'field.returnedOn': 'Returned on',

  'login.title': 'Sign in',
  'login.subtitle': 'Use the credentials issued by your department administrator.',
  'login.username': 'Username',
  'login.password': 'Password',
  'login.failed': 'Could not sign in',
  'login.lockoutNotice':
    'After 5 failed attempts an account is locked for 15 minutes. If sign-in keeps failing, wait and try again, or contact your administrator.',

  'password.title': 'Change your password',
  'password.required': 'You must set a new password before continuing.',
  'password.current': 'Current password',
  'password.new': 'New password',
  'password.confirm': 'Confirm new password',
  'password.hint': 'At least 10 characters, with an uppercase letter, a lowercase letter and a digit.',
  'password.changed': 'Password changed. Please sign in again.',

  'search.placeholder': 'Search by name, file number, plot, scheme or lease A/C number',
  'search.hint': 'Try a name, a file number, or a lease account number.',
  'search.results': 'results',
  'search.noResults': 'No files matched',
  'search.noResultsHint': 'Check the spelling, or search by file number instead.',
  'search.filters': 'Filters',
  'search.strategy.exact': 'Exact match on file identifier',
  'search.strategy.fulltext': 'Text search',
  'search.strategy.fuzzy': 'Closest matches — no exact result found',
  'search.strategy.browse': 'Browsing all files',
  'search.took': 'in',

  'dashboard.totalFiles': 'Total files',
  'dashboard.available': 'On the shelf',
  'dashboard.issued': 'Currently out',
  'dashboard.overdue': 'Overdue',
  'dashboard.missing': 'Missing',
  'dashboard.addedThisMonth': 'Added this month',
  'dashboard.movementsThisMonth': 'Movements this month',
  'dashboard.topBorrowers': 'Most files taken (90 days)',
  'dashboard.byRoom': 'Files by record room',
  'dashboard.recentlyIssued': 'Recently issued',
  'dashboard.quickActions': 'Quick actions',

  'issue.title': 'Issue file',
  'issue.recipient': 'Issue to',
  'issue.selectRecipient': 'Select an employee',
  'issue.purposeNote': 'Note (optional)',
  'issue.sendOtp': 'Send verification code',
  'issue.sending': 'Sending…',
  'issue.otpSent': 'A 6-digit code has been sent to',
  'issue.otpLabel': 'Verification code',
  'issue.verify': 'Verify and issue',
  'issue.verifying': 'Verifying…',
  'issue.resend': 'Send a new code',
  'issue.expiresIn': 'Code expires in',
  'issue.devCode': 'Development code',
  'issue.success': 'File issued',

  'return.title': 'Return file',
  'return.condition': 'Condition',
  'return.condition.GOOD': 'Good',
  'return.condition.DAMAGED': 'Damaged',
  'return.condition.INCOMPLETE': 'Pages missing',
  'return.relocate': 'Re-shelve at a different location',
  'return.note': 'Note (optional)',
  'return.success': 'File returned',

  'picklist.title': 'Record room pick list',
  'picklist.truncated': 'showing first {shown} of {total} — narrow the search for a shorter list',
  'picklist.collectedBy': 'Collected by',
  'picklist.date': 'Date',
  'action.pickList': 'Print pick list',
  'action.exportCsv': 'Download CSV',
  'action.printForm': 'Print filled form',
  'demo.title': 'Demonstration',
  'demo.body':
    'Sample data, and the issue OTP is shown on screen instead of sent by SMS. Do not enter real Patta records here.',
  'export.tooMany':
    'This search matches {total} files and the download stops at {cap} rows. Narrow it first, or continue and get the first {cap}?',
  'action.select': 'Select',
  'action.selectAll': 'Select all on this page',
  'bulk.selected': '{n} file(s) selected',
  'bulk.moveTo': 'Move to a shelf',
  'bulk.moveTitle': 'Move {n} file(s)',
  'bulk.moved': '{n} file(s) moved',
  'file.possibleDuplicate': 'A file like this already exists',
  'file.duplicateHint': 'Check it is not the same file before saving. You can still go ahead.',
  'file.reason.sameFileNumber': 'same file number',
  'file.reason.sameLeaseAccount': 'same lease A/C number',
  'file.reason.samePlotAndName': 'same plot and a similar name',
  'file.saveAndNext': 'Save and add another',
  'import.newLocations': 'New almirahs',
  'import.newLocationsHint': 'This sheet names shelves that are not on record yet. They will be created on import — check them against the room first.',
  'location.known': 'This shelf is already on record.',
  'location.willBeCreated': 'No such shelf on record yet — saving will create it. Check the codes against the almirah before you continue.',
  'location.newCodeWarning': '"{code}" is not an existing code here. If that is a typo, correct it now.',
  'location.title': 'Almirahs and shelves',
  'location.subtitle': 'Define the record room before the survey starts, so clerks pick a shelf instead of typing one.',
  'location.addAlmirah': 'Add an almirah',
  'location.room': 'Room',
  'location.almirah': 'Almirah (rack)',
  'location.columnsLabel': 'Columns',
  'location.bagsLabel': 'Bags in each column',
  'location.rangeHint': 'Comma separated, or a range like C-01..C-06',
  'location.created': '{n} shelves created',
  'location.rename': 'Rename',
  'location.renameTitle': 'Rename a code everywhere',
  'location.renameHint': 'Every file on these shelves follows the new code. If the new code already exists the two are merged.',
  'location.renamed': 'Renamed — {files} file(s) affected',
  'location.typoReport': 'Possible typos',
  'location.typoHint': 'Codes that look almost identical in the same room, where one holds almost nothing. Usually a mistyped almirah.',
  'location.noTypos': 'No suspicious codes found.',
  'location.filesHere': 'files',
  'location.emptyShelf': 'empty',
  'location.deleteBag': 'Remove',
  'location.moveContents': 'Move contents',
  'location.from': 'From',
  'location.to': 'To',
  'location.moved': '{n} file(s) moved',
  'nav.locations2': 'Almirahs',
  'file.formType': 'Form type',
  'file.new': 'Add a file',
  'file.edit': 'Edit file',
  'file.identity': 'File identity',
  'file.classification': 'Department and ward',
  'file.property': 'Property details',
  'file.locationSection': 'Where the file is kept',
  'file.notes': 'Remarks',
  'file.kindHint': 'Form 1 is for general files. Form 2 adds scheme, society, plot and allottee.',
  'file.locationHint': 'Exactly as written on the rack and the bag.',
  'file.created': 'File added',
  'file.updated': 'File updated',
  'file.markStatus': 'Change status',
  'file.statusReason': 'Reason',
  'file.statusReasonHint': 'Recorded in the audit trail and added to the remarks. Required.',
  'file.statusChanged': 'Status updated',
  'field.fileName': 'File name',
  'field.oicName': 'OIC name',
  'field.schemeNo': 'Scheme no.',
  'field.sector': 'Sector',
  'field.houseNo': 'House no.',
  'field.leaseDescription': 'Address / lease description',
  'field.subjectHi': 'Subject (Hindi)',
  'field.allotteeHi': 'First allottee (Hindi)',
  'field.schemeHi': 'Scheme (Hindi)',

  'history.title': 'Movement history',
  'history.empty': 'This file has never been issued.',

  'import.title': 'Import files from a survey sheet',
  'import.step1': 'Download the template',
  'import.step1Hint':
    'Give this sheet to the survey staff. Column order matches the printed ISO forms.',
  'import.form1': 'Form 1 — General files',
  'import.form2': 'Form 2 — Property / lease files',
  'import.step2': 'Upload the filled sheet',
  'import.step2Hint': 'Nothing is saved until you review the check below and confirm.',
  'import.choose': 'Choose a file',
  'import.checking': 'Checking the sheet…',
  'import.totalRows': 'Rows found',
  'import.validRows': 'Ready to import',
  'import.errorRows': 'With problems',
  'import.duplicateRows': 'Already in the system',
  'import.commit': 'Import the valid rows',
  'import.committing': 'Importing…',
  'import.discard': 'Discard',
  'import.problems': 'Rows that need fixing',
  'import.row': 'Row',
  'import.column': 'Column',
  'import.problem': 'Problem',
  'import.done': 'Imported',
  'import.history': 'Previous imports',
  'import.sheet': 'Sheet',

  'users.title': 'Users',
  'users.add': 'Add user',
  'users.name': 'Name',
  'users.username': 'Username',
  'users.mobile': 'Mobile',
  'users.role': 'Role',
  'users.designation': 'Designation',
  'users.lastLogin': 'Last sign-in',
  'users.openFiles': 'Files out',
  'users.active': 'Active',
  'users.inactive': 'Deactivated',
  'users.showInactive': 'Show deactivated',
  'users.deactivate': 'Deactivate',
  'users.activate': 'Reactivate',
  'users.resetPassword': 'Reset password',
  'users.tempPassword': 'Temporary password',
  'users.tempPasswordHint':
    'Note this down now and hand it over. It cannot be shown again, and must be changed at first sign-in.',

  'reports.activity': 'Staff activity',
  'reports.days': 'Last {n} days',
  'reports.issued': 'Taken',
  'reports.returned': 'Returned',
  'reports.outstanding': 'Still out',

  'audit.title': 'Audit trail',
  'audit.when': 'When',
  'audit.who': 'Who',
  'audit.what': 'Action',
  'audit.detail': 'Detail',
  'audit.entity': 'Record type',
  'audit.changes': 'What changed',
  'audit.showChanges': 'Show changes',
  'audit.hideChanges': 'Hide changes',
  'audit.noFieldChanges': 'This action records no field-level changes.',
  'audit.was': 'was',
  'audit.now': 'now',
  'audit.forThisFile': 'Changes to this record',
  'audit.entity.file_record': 'Files (entry, edit, issue, return, move)',
  'audit.entity.storage_bag': 'Almirahs',
  'audit.entity.file_movement': 'OTP messages',
  'audit.entity.app_user': 'Users',
  'audit.entity.import_batch': 'Imports',

  'locations.title': 'Record rooms',
  'locations.rack': 'rack',
  'locations.racks': 'racks',
  'locations.columns': 'columns',
  'locations.file': 'file',
  'locations.files': 'files',
  'locations.emptyBag': 'empty',

  'notFound.title': 'Page not found',
  'notFound.hint': 'The address you opened does not exist. It may be an old bookmark or a mistyped link.',

  'confirm.title': 'Please confirm',
  'confirm.deactivate': 'Deactivate {name}? They will be signed out immediately and will not be able to sign in again until reactivated.',
  'confirm.resetPassword': 'Reset the password for {name}? Their current password stops working at once, they are signed out everywhere, and you must hand them the new temporary password.',
  'confirm.proceed': 'Yes, continue',

  'common.loading': 'Loading…',
  'common.error': 'Something went wrong',
  'common.none': 'None',
  'common.all': 'All',
  'common.yes': 'Yes',
  'common.no': 'No',
  'common.required': 'Required',
  'common.optional': 'optional',
  'common.of': 'of',
  'common.language': 'भाषा / Language',
} as const;

export type MessageKey = keyof typeof EN;

const HI: Record<MessageKey, string> = {
  'app.name': 'पट्टा अभिलेख',
  'app.org': 'नगर निगम जयपुर',
  'app.tagline': 'फाइल एवं पट्टा अभिलेख प्रबंधन',

  'nav.dashboard': 'डैशबोर्ड',
  'nav.search': 'फाइल खोजें',
  'nav.locations': 'अभिलेख कक्ष',
  'nav.issued': 'निर्गत फाइलें',
  'nav.import': 'आयात',
  'nav.users': 'उपयोगकर्ता',
  'nav.reports': 'रिपोर्ट',
  'nav.audit': 'अंकेक्षण विवरण',

  'action.search': 'खोजें',
  'action.cancel': 'निरस्त',
  'action.save': 'सहेजें',
  'action.saving': 'सहेजा जा रहा है…',
  'action.close': 'बंद करें',
  'action.issue': 'फाइल निर्गत करें',
  'action.return': 'फाइल वापस लें',
  'action.back': 'वापस',
  'action.signIn': 'साइन इन',
  'action.signOut': 'साइन आउट',
  'action.retry': 'पुनः प्रयास करें',
  'action.clear': 'हटाएं',
  'action.edit': 'संपादित करें',
  'action.viewAll': 'सभी देखें',
  'action.download': 'डाउनलोड',
  'action.next': 'अगला',
  'action.previous': 'पिछला',
  'action.loadMore': 'और दिखाएं',
  'action.print': 'प्रिंट',

  'status.AVAILABLE': 'उपलब्ध',
  'status.ISSUED': 'निर्गत',
  'status.MISSING': 'अनुपलब्ध',
  'status.WEEDED': 'नष्टीकृत',
  'status.overdue': 'अतिदेय',

  'kind.GENERAL': 'सामान्य फाइल',
  'kind.PROPERTY': 'सम्पत्ति फाइल',

  'field.eFileNumber': 'ई-फाइल संख्या',
  'field.fileNumber': 'फाइल संख्या',
  'field.subject': 'विषय',
  'field.allottee': 'प्रथम आवंटी',
  'field.plotNo': 'भूखंड संख्या',
  'field.scheme': 'योजना',
  'field.society': 'सोसायटी',
  'field.ward': 'वार्ड',
  'field.zone': 'जोन',
  'field.department': 'विभाग',
  'field.year': 'वर्ष',
  'field.location': 'स्थान',
  'field.status': 'स्थिति',
  'field.leaseAcNo': 'लीज खाता संख्या',
  'field.remark': 'टिप्पणी',
  'field.room': 'कक्ष',
  'field.rack': 'रैक',
  'field.column': 'कॉलम',
  'field.bag': 'बैग',
  'field.purpose': 'प्रयोजन',
  'field.expectedReturn': 'अपेक्षित वापसी',
  'field.issuedTo': 'किसे दी गई',
  'field.issuedBy': 'किसने दी',
  'field.issuedOn': 'निर्गत दिनांक',
  'field.returnedOn': 'वापसी दिनांक',

  'login.title': 'साइन इन करें',
  'login.subtitle': 'विभागीय प्रशासक द्वारा जारी लॉगिन विवरण का उपयोग करें।',
  'login.username': 'उपयोगकर्ता नाम',
  'login.password': 'पासवर्ड',
  'login.failed': 'साइन इन नहीं हो सका',
  'login.lockoutNotice':
    '5 बार गलत प्रयास के बाद खाता 15 मिनट के लिए बंद हो जाता है। बार-बार विफल होने पर प्रतीक्षा करें, या प्रशासक से संपर्क करें।',

  'password.title': 'अपना पासवर्ड बदलें',
  'password.required': 'आगे बढ़ने से पहले नया पासवर्ड बनाना आवश्यक है।',
  'password.current': 'वर्तमान पासवर्ड',
  'password.new': 'नया पासवर्ड',
  'password.confirm': 'नया पासवर्ड दोहराएं',
  'password.hint': 'कम से कम 10 अक्षर, जिनमें एक बड़ा अक्षर, एक छोटा अक्षर और एक अंक हो।',
  'password.changed': 'पासवर्ड बदल दिया गया। कृपया पुनः साइन इन करें।',

  'search.placeholder': 'नाम, फाइल संख्या, भूखंड, योजना या लीज खाता संख्या से खोजें',
  'search.hint': 'नाम, फाइल संख्या या लीज खाता संख्या से खोजें।',
  'search.results': 'परिणाम',
  'search.noResults': 'कोई फाइल नहीं मिली',
  'search.noResultsHint': 'वर्तनी जांचें, या फाइल संख्या से खोजें।',
  'search.filters': 'फ़िल्टर',
  'search.strategy.exact': 'फाइल संख्या से सटीक मिलान',
  'search.strategy.fulltext': 'पाठ खोज',
  'search.strategy.fuzzy': 'निकटतम मिलान — सटीक परिणाम नहीं मिला',
  'search.strategy.browse': 'सभी फाइलें',
  'search.took': 'समय',

  'dashboard.totalFiles': 'कुल फाइलें',
  'dashboard.available': 'अलमारी में',
  'dashboard.issued': 'वर्तमान में बाहर',
  'dashboard.overdue': 'अतिदेय',
  'dashboard.missing': 'अनुपलब्ध',
  'dashboard.addedThisMonth': 'इस माह जोड़ी गईं',
  'dashboard.movementsThisMonth': 'इस माह आवाजाही',
  'dashboard.topBorrowers': 'सर्वाधिक फाइलें ली गईं (90 दिन)',
  'dashboard.byRoom': 'कक्ष अनुसार फाइलें',
  'dashboard.recentlyIssued': 'हाल ही में निर्गत',
  'dashboard.quickActions': 'त्वरित कार्य',

  'issue.title': 'फाइल निर्गत करें',
  'issue.recipient': 'किसे देनी है',
  'issue.selectRecipient': 'कर्मचारी चुनें',
  'issue.purposeNote': 'टिप्पणी (वैकल्पिक)',
  'issue.sendOtp': 'सत्यापन कोड भेजें',
  'issue.sending': 'भेजा जा रहा है…',
  'issue.otpSent': '6 अंकों का कोड भेजा गया है',
  'issue.otpLabel': 'सत्यापन कोड',
  'issue.verify': 'सत्यापित कर निर्गत करें',
  'issue.verifying': 'सत्यापन हो रहा है…',
  'issue.resend': 'नया कोड भेजें',
  'issue.expiresIn': 'कोड समाप्त होगा',
  'issue.devCode': 'विकास कोड',
  'issue.success': 'फाइल निर्गत की गई',

  'return.title': 'फाइल वापस लें',
  'return.condition': 'स्थिति',
  'return.condition.GOOD': 'ठीक',
  'return.condition.DAMAGED': 'क्षतिग्रस्त',
  'return.condition.INCOMPLETE': 'पृष्ठ गायब',
  'return.relocate': 'दूसरे स्थान पर रखें',
  'return.note': 'टिप्पणी (वैकल्पिक)',
  'return.success': 'फाइल वापस ली गई',

  'picklist.title': 'अभिलेख कक्ष सूची',
  'picklist.truncated': '{total} में से पहले {shown} — छोटी सूची के लिए खोज सीमित करें',
  'picklist.collectedBy': 'प्राप्तकर्ता',
  'picklist.date': 'दिनांक',
  'action.pickList': 'सूची प्रिंट करें',
  'action.exportCsv': 'CSV डाउनलोड करें',
  'action.printForm': 'भरा हुआ फॉर्म प्रिंट करें',
  'demo.title': 'प्रदर्शन',
  'demo.body':
    'नमूना डेटा। ओ.टी.पी. एस.एम.एस. के बजाय स्क्रीन पर दिखाया जाता है। यहाँ वास्तविक पट्टा अभिलेख दर्ज न करें।',
  'export.tooMany':
    'इस खोज में {total} फाइलें हैं, डाउनलोड {cap} पंक्तियों पर रुक जाएगा। पहले खोज सीमित करें, या पहली {cap} पंक्तियाँ लें?',
  'action.select': 'चुनें',
  'action.selectAll': 'इस पृष्ठ की सभी पंक्तियाँ चुनें',
  'bulk.selected': '{n} फाइल चयनित',
  'bulk.moveTo': 'अलमारी में स्थानांतरित करें',
  'bulk.moveTitle': '{n} फाइल स्थानांतरित करें',
  'bulk.moved': '{n} फाइल स्थानांतरित',
  'file.possibleDuplicate': 'ऐसी फाइल पहले से मौजूद है',
  'file.duplicateHint': 'सहेजने से पहले जांचें कि यह वही फाइल तो नहीं। आप फिर भी आगे बढ़ सकते हैं।',
  'file.reason.sameFileNumber': 'वही फाइल संख्या',
  'file.reason.sameLeaseAccount': 'वही लीज खाता संख्या',
  'file.reason.samePlotAndName': 'वही भूखंड एवं मिलता-जुलता नाम',
  'file.saveAndNext': 'सहेजें और अगली जोड़ें',
  'import.newLocations': 'नई अलमारियाँ',
  'import.newLocationsHint': 'इस शीट में ऐसी शेल्फ हैं जो दर्ज नहीं हैं। आयात पर बन जाएँगी — पहले कक्ष से मिलान करें।',
  'location.known': 'यह शेल्फ पहले से दर्ज है।',
  'location.willBeCreated': 'यह शेल्फ अभी दर्ज नहीं है — सहेजने पर बन जाएगी। आगे बढ़ने से पहले अलमारी पर लिखे कोड से मिलान करें।',
  'location.newCodeWarning': '"{code}" यहाँ मौजूदा कोड नहीं है। यदि यह गलती है तो अभी सुधारें।',
  'location.title': 'अलमारियाँ एवं शेल्फ',
  'location.subtitle': 'सर्वे शुरू होने से पहले अभिलेख कक्ष दर्ज करें, ताकि कर्मचारी शेल्फ टाइप करने के बजाय चुनें।',
  'location.addAlmirah': 'अलमारी जोड़ें',
  'location.room': 'कक्ष',
  'location.almirah': 'अलमारी (रैक)',
  'location.columnsLabel': 'कॉलम',
  'location.bagsLabel': 'प्रत्येक कॉलम में बैग',
  'location.rangeHint': 'अल्पविराम से अलग, या C-01..C-06 जैसी श्रेणी',
  'location.created': '{n} शेल्फ बनाई गईं',
  'location.rename': 'नाम बदलें',
  'location.renameTitle': 'कोड हर जगह बदलें',
  'location.renameHint': 'इन शेल्फ की सभी फाइलें नए कोड पर चली जाएँगी। यदि नया कोड पहले से है तो दोनों मिला दिए जाएँगे।',
  'location.renamed': 'नाम बदला — {files} फाइलें प्रभावित',
  'location.typoReport': 'संभावित त्रुटियाँ',
  'location.typoHint': 'एक ही कक्ष में लगभग एक जैसे कोड, जिनमें एक लगभग खाली है। प्रायः गलत टाइप की गई अलमारी।',
  'location.noTypos': 'कोई संदिग्ध कोड नहीं मिला।',
  'location.filesHere': 'फाइलें',
  'location.emptyShelf': 'खाली',
  'location.deleteBag': 'हटाएं',
  'location.moveContents': 'सामग्री स्थानांतरित करें',
  'location.from': 'से',
  'location.to': 'को',
  'location.moved': '{n} फाइलें स्थानांतरित',
  'nav.locations2': 'अलमारियाँ',
  'file.formType': 'फॉर्म प्रकार',
  'file.new': 'नई फाइल जोड़ें',
  'file.edit': 'फाइल संपादित करें',
  'file.identity': 'फाइल विवरण',
  'file.classification': 'विभाग एवं वार्ड',
  'file.property': 'सम्पत्ति विवरण',
  'file.locationSection': 'फाइल कहाँ रखी है',
  'file.notes': 'टिप्पणी',
  'file.kindHint': 'फॉर्म 1 सामान्य फाइलों के लिए। फॉर्म 2 में योजना, सोसायटी, भूखंड एवं आवंटी भी हैं।',
  'file.locationHint': 'रैक एवं बैग पर लिखे अनुसार ही भरें।',
  'file.created': 'फाइल जोड़ी गई',
  'file.updated': 'फाइल अद्यतन की गई',
  'file.markStatus': 'स्थिति बदलें',
  'file.statusReason': 'कारण',
  'file.statusReasonHint': 'अंकेक्षण विवरण में दर्ज होगा और टिप्पणी में जुड़ेगा। आवश्यक है।',
  'file.statusChanged': 'स्थिति बदली गई',
  'field.fileName': 'फाइल का नाम',
  'field.oicName': 'ओ.आई.सी. नाम',
  'field.schemeNo': 'योजना संख्या',
  'field.sector': 'सेक्टर',
  'field.houseNo': 'मकान संख्या',
  'field.leaseDescription': 'पता / लीज विवरण',
  'field.subjectHi': 'विषय (हिंदी)',
  'field.allotteeHi': 'प्रथम आवंटी (हिंदी)',
  'field.schemeHi': 'योजना (हिंदी)',

  'history.title': 'आवाजाही विवरण',
  'history.empty': 'यह फाइल कभी निर्गत नहीं हुई।',

  'import.title': 'सर्वे शीट से फाइलें आयात करें',
  'import.step1': 'टेम्पलेट डाउनलोड करें',
  'import.step1Hint': 'यह शीट सर्वे कर्मचारियों को दें। कॉलम क्रम मुद्रित ISO फॉर्म जैसा ही है।',
  'import.form1': 'फॉर्म 1 — सामान्य फाइलें',
  'import.form2': 'फॉर्म 2 — सम्पत्ति / पट्टा फाइलें',
  'import.step2': 'भरी हुई शीट अपलोड करें',
  'import.step2Hint': 'जांच देखकर पुष्टि करने तक कुछ भी सहेजा नहीं जाएगा।',
  'import.choose': 'फाइल चुनें',
  'import.checking': 'शीट जांची जा रही है…',
  'import.totalRows': 'कुल पंक्तियां',
  'import.validRows': 'आयात के लिए तैयार',
  'import.errorRows': 'त्रुटि वाली',
  'import.duplicateRows': 'पहले से मौजूद',
  'import.commit': 'सही पंक्तियां आयात करें',
  'import.committing': 'आयात हो रहा है…',
  'import.discard': 'निरस्त करें',
  'import.problems': 'सुधार आवश्यक पंक्तियां',
  'import.row': 'पंक्ति',
  'import.column': 'कॉलम',
  'import.problem': 'समस्या',
  'import.done': 'आयात पूर्ण',
  'import.history': 'पूर्व आयात',
  'import.sheet': 'शीट',

  'users.title': 'उपयोगकर्ता',
  'users.add': 'उपयोगकर्ता जोड़ें',
  'users.name': 'नाम',
  'users.username': 'उपयोगकर्ता नाम',
  'users.mobile': 'मोबाइल',
  'users.role': 'भूमिका',
  'users.designation': 'पदनाम',
  'users.lastLogin': 'अंतिम साइन इन',
  'users.openFiles': 'बाहर फाइलें',
  'users.active': 'सक्रिय',
  'users.inactive': 'निष्क्रिय',
  'users.showInactive': 'निष्क्रिय दिखाएं',
  'users.deactivate': 'निष्क्रिय करें',
  'users.activate': 'पुनः सक्रिय करें',
  'users.resetPassword': 'पासवर्ड रीसेट करें',
  'users.tempPassword': 'अस्थायी पासवर्ड',
  'users.tempPasswordHint':
    'इसे अभी नोट कर लें और सौंप दें। यह दोबारा नहीं दिखाया जाएगा, और पहली बार साइन इन पर बदलना होगा।',

  'reports.activity': 'कर्मचारी गतिविधि',
  'reports.days': 'पिछले {n} दिन',
  'reports.issued': 'ली गईं',
  'reports.returned': 'वापस कीं',
  'reports.outstanding': 'अब भी बाहर',

  'audit.title': 'अंकेक्षण विवरण',
  'audit.when': 'कब',
  'audit.who': 'किसने',
  'audit.what': 'कार्य',
  'audit.detail': 'विवरण',
  'audit.entity': 'अभिलेख प्रकार',
  'audit.changes': 'क्या बदला',
  'audit.showChanges': 'परिवर्तन देखें',
  'audit.hideChanges': 'परिवर्तन छिपाएँ',
  'audit.noFieldChanges': 'इस कार्य में किसी क्षेत्र का परिवर्तन दर्ज नहीं है।',
  'audit.was': 'पहले',
  'audit.now': 'अब',
  'audit.forThisFile': 'इस अभिलेख में परिवर्तन',
  'audit.entity.file_record': 'फाइलें (प्रविष्टि, संशोधन, निर्गम, वापसी, स्थानांतरण)',
  'audit.entity.storage_bag': 'अलमारियाँ',
  'audit.entity.file_movement': 'ओ.टी.पी. संदेश',
  'audit.entity.app_user': 'उपयोगकर्ता',
  'audit.entity.import_batch': 'आयात',

  'locations.title': 'अभिलेख कक्ष',
  // Hindi has no plural inflection for these counters, so both forms are the
  // same word -- the key pair exists for English.
  'locations.rack': 'रैक',
  'locations.racks': 'रैक',
  'locations.columns': 'कॉलम',
  'locations.file': 'फाइल',
  'locations.files': 'फाइलें',
  'locations.emptyBag': 'खाली',

  'notFound.title': 'पृष्ठ नहीं मिला',
  'notFound.hint': 'यह पता मौजूद नहीं है। संभवतः पुराना बुकमार्क या गलत लिंक है।',

  'confirm.title': 'कृपया पुष्टि करें',
  'confirm.deactivate': '{name} को निष्क्रिय करें? वे तुरंत साइन आउट हो जाएंगे और पुनः सक्रिय होने तक साइन इन नहीं कर सकेंगे।',
  'confirm.resetPassword': '{name} का पासवर्ड रीसेट करें? उनका वर्तमान पासवर्ड तुरंत बंद हो जाएगा, वे हर जगह साइन आउट हो जाएंगे, और आपको नया अस्थायी पासवर्ड उन्हें देना होगा।',
  'confirm.proceed': 'हां, जारी रखें',

  'common.loading': 'लोड हो रहा है…',
  'common.error': 'कुछ गड़बड़ हुई',
  'common.none': 'कोई नहीं',
  'common.all': 'सभी',
  'common.yes': 'हां',
  'common.no': 'नहीं',
  'common.required': 'आवश्यक',
  'common.optional': 'वैकल्पिक',
  'common.of': 'में से',
  'common.language': 'भाषा / Language',
};

export type Language = 'en' | 'hi';

const STORAGE_KEY = 'jmc.language';

interface I18nValue {
  lang: Language;
  setLang: (lang: Language) => void;
  t: (key: MessageKey, vars?: Record<string, string | number>) => string;
  /** Picks the Hindi value when the interface is Hindi and one exists. */
  pick: (en: string | null | undefined, hi: string | null | undefined) => string;
}

const I18nContext = createContext<I18nValue | null>(null);

function readStored(): Language {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored === 'hi' ? 'hi' : 'en';
  } catch {
    // Private browsing or blocked storage: English is a safe default.
    return 'en';
  }
}

export function I18nProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<Language>(readStored);

  const setLang = useCallback((next: Language) => {
    setLangState(next);
    document.documentElement.lang = next;
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // Not being able to remember the choice is not worth an error.
    }
  }, []);

  const value = useMemo<I18nValue>(() => {
    const table = lang === 'hi' ? HI : EN;
    return {
      lang,
      setLang,
      t: (key, vars) => {
        let text: string = table[key] ?? EN[key] ?? key;
        if (vars) {
          for (const [name, replacement] of Object.entries(vars)) {
            text = text.replaceAll(`{${name}}`, String(replacement));
          }
        }
        return text;
      },
      pick: (en, hi) => (lang === 'hi' ? (hi ?? en ?? '') : (en ?? hi ?? '')),
    };
  }, [lang, setLang]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nValue {
  const context = useContext(I18nContext);
  if (!context) throw new Error('useI18n must be used inside <I18nProvider>');
  return context;
}
